/**
 * Area Groups API — all backend calls for the Area Groups tab.
 *
 * Each function maps directly to a V1 endpoint / stored procedure
 * but uses the new V2 api client for cleaner code.
 *
 * Endpoints & payloads are identical to V1 for full backend compatibility.
 */

import { apiFetch, apiTransaction } from '../../../../services/api.js';

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/**
 * Fetch all area groups with their areas for a campus.
 * V1 procedure: sp_dev_getareagroupandareabycampusid
 *
 * @param {number} campusId
 * @param {boolean} [readOnly=true] — false on first load to bust cache
 * @returns {Promise<Array>}
 */
export function fetchAreaGroups(campusId, readOnly = true) {
  return apiFetch({
    procedure: 'sp_dev_getareagroupandareabycampusid',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly,
  });
}

// ---------------------------------------------------------------------------
// WRITE operations
// ---------------------------------------------------------------------------

/**
 * Create a new area group.
 * V1 TransType: area_group_add
 *
 * @param {object} data — { area_group_name, area_group_description, area_ids }
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function addAreaGroup(data, session) {
  const payload = [{
    campus_id: session.campusId,
    area_group_name: data.area_group_name,
    area_group_description: data.area_group_description || '',
    area_ids: data.area_ids || [],
  }];

  return apiTransaction({
    transType: 'area_group_add',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Edit an existing area group.
 * V1 TransType: area_group_edit
 *
 * @param {object} data — Updated group data
 * @param {object} oldData — Original group data (for change tracking)
 * @param {object} session
 * @returns {Promise<[boolean, string?]>}
 */
export function editAreaGroup(data, oldData, session) {
  const payload = [{
    area_group_name: data.area_group_name,
    area_group_id: parseInt(data.area_group_id),
    area_group_description: data.area_group_description || '',
    area_ids: data.area_ids || [],
    campus_id: session.campusId,
  }];

  const oldPayload = [{
    area_group_name: oldData.area_group_name,
    area_group_id: parseInt(oldData.area_group_id),
    area_group_description: oldData.area_group_description || '',
    area_ids: oldData.area_ids || [],
    campus_id: session.campusId,
  }];

  return apiTransaction({
    transType: 'area_group_edit',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Delete one or more area groups.
 * V1 TransType: area_group_delete
 *
 * @param {Array<number>} groupIds
 * @param {object} session
 * @returns {Promise<[boolean, string?]>}
 */
export function deleteAreaGroups(groupIds, session) {
  const payload = groupIds.map((id) => ({
    area_group_id: parseInt(id),
  }));

  return apiTransaction({
    transType: 'area_group_delete',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}
